import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

// Context Providers
import { AuthProvider } from "@/contexts/AuthContext";

// Hooks
import useReferralCapture from "@/hooks/useReferralCapture";

// Layouts
import MainLayout from "@/layouts/MainLayout";
import AuthLayout from "@/layouts/AuthLayout";
import PublicLayout from "@/layouts/PublicLayout";
import AdminLayout from "@/layouts/AdminLayout";
import SurveyMinimalLayout from "@/layouts/SurveyMinimalLayout";

// Public pages (no auth required)
import HomePage from "@/pages/HomePage";
import EventsPublicPage from "@/pages/EventsPublicPage";
import EventDetailPage from "@/pages/EventDetailPage";
import RsvpMultiStepPage from "@/pages/RsvpMultiStepPage";
import RsvpEditPage from "@/pages/RsvpEditPage";
import RsvpVerifyPage from "@/pages/RsvpVerifyPage";
import ProfileCompletionGate from "@/pages/ProfileCompletionGate";
import RsvpResumePage from "@/pages/RsvpResumePage";
import MagicLinkPage from "@/pages/MagicLinkPage";
import SetPasswordPage from "@/pages/SetPasswordPage";
import SecuritySettingsPage from "@/pages/SecuritySettingsPage";

// Auth-gated pages
import ProfilePage from "@/pages/ProfilePage";
import OnboardingPage from "@/pages/OnboardingPage";
import VerificationPage from "@/pages/VerificationPage";
import MyEventsPage from "@/pages/MyEventsPage";

// Auth-gated but kept from Phase 0 (Restore in Phase 8)
import MatchesPage from "@/pages/MatchesPage";
import MessagesPage from "@/pages/MessagesPage";

// Admin pages
import AdminEventsListPage from "@/pages/admin/AdminEventsListPage";
import AdminEventEditPage from "@/pages/admin/AdminEventEditPage";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
// Bundle 161 - Incomplete-profile reminder + manual deletion.
import AdminIncompleteProfilesPage from "@/pages/admin/AdminIncompleteProfilesPage";
import AdminBrokenNamesPage from "@/pages/admin/AdminBrokenNamesPage";
import AdminUserDetailPage from "@/pages/admin/AdminUserDetailPage";
import AdminUserRolesPage from "@/pages/admin/AdminUserRolesPage";
import AdminRegistrationsPage from "@/pages/admin/AdminRegistrationsPage";
import AdminTaxonomiesPage from "@/pages/admin/AdminTaxonomiesPage";
import AdminAnalyticsPage from "@/pages/admin/AdminAnalyticsPage";
import AdminProgressionPage from "@/pages/admin/AdminProgressionPage";
import AdminReferralsPage from "@/pages/admin/AdminReferralsPage";
import AdminMaintenancePage from "@/pages/admin/AdminMaintenancePage";
import AdminIncidentsPage from "@/pages/admin/AdminIncidentsPage";
import EventReconciliationPage from "@/pages/admin/EventReconciliationPage";
import IntroSuggestionConfirmPage from "@/pages/IntroSuggestionConfirmPage";
import AdminDataHealthPage from "@/pages/admin/AdminDataHealthPage";
import AdminHealthDrilldownPage from "@/pages/admin/AdminHealthDrilldownPage";
import AdminIntegrationsPage from "@/pages/admin/AdminIntegrationsPage";
import AdminAuditPage from "@/pages/admin/AdminAuditPage";
import AdminPricingAuditPage from "@/pages/admin/AdminPricingAuditPage";
import AdminBrokenMediaReportPage from "@/pages/admin/AdminBrokenMediaReportPage";
import AdminPixelsPage from "@/pages/admin/AdminPixelsPage";
import ProgressionGuidePage from "@/pages/ProgressionGuidePage";
import AdminVerificationPage from "@/pages/AdminVerificationPage";
import AdminPaymentsPage from "@/pages/admin/AdminPaymentsPage";
import AdminPaymentsByEventPage from "@/pages/admin/AdminPaymentsByEventPage";
import AdminSurveyResultsPage from "@/pages/admin/AdminSurveyResultsPage";
// Bundle 98 - incident admin page + replaced-token FE fallback.
import IncidentLegacySurveyRecipientsPage from "@/pages/admin/IncidentLegacySurveyRecipientsPage";
// Bundle 101 - Survey Ops landing + per-event Respondents/Campaign.
import AdminSurveysLandingPage from "@/pages/admin/AdminSurveysLandingPage";
import AdminSurveyRespondentsPage from "@/pages/admin/AdminSurveyRespondentsPage";
import AdminSurveyCampaignPage from "@/pages/admin/AdminSurveyCampaignPage";
import AdminIncidentPreBundle89Page from "@/pages/admin/AdminIncidentPreBundle89Page";
// Bundle 105 - Global drift-scan admin page.
import AdminDriftScanPage from "@/pages/admin/AdminDriftScanPage";
// Bundle 106 - Editable email templates.
import AdminEmailTemplatesPage from "@/pages/admin/AdminEmailTemplatesPage";
// Bundle 108 fold-in - read-only debug page for campaign invariant.
import AdminDebugCampaignsPage from "@/pages/admin/AdminDebugCampaignsPage";
// Bundle 102 - Follow-ups draft-first review page.
import AdminFollowupsPendingPage from "@/pages/admin/AdminFollowupsPendingPage";
import SurveyReplacedPage from "@/pages/SurveyReplacedPage";
import AdminConnectionsPage from "@/pages/admin/AdminConnectionsPage";
import AdminSeatingPage from "@/pages/admin/AdminSeatingPage";
import AdminEventNudgePage from "@/pages/admin/AdminEventNudgePage";
import AdminZelleAutoVerifyPage from "@/pages/admin/AdminZelleAutoVerifyPage";

// Phase 10 pages
import CheckInPage from "@/pages/CheckInPage";
import SurveyPage from "@/pages/SurveyPage";

// Auth pages
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AuthCallback from "@/pages/AuthCallback";

// Payments (Phase 5)
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";

// Bundle 75 Task 4 - public compliance pages.
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
// Bundle 138 - Stripe compliance package: AUP + Pricing.
import AcceptableUsePolicyPage from "@/pages/AcceptableUsePolicyPage";
import PricingPage from "@/pages/PricingPage";

// Bundle 78 Task 2 - Law Enforcement Protocols page (Stripe compliance).
import LawEnforcementPage from "@/pages/LawEnforcementPage";

// Bundle 78 Task 1 - Missed connections public form + admin queue.
import MissedConnectionRequestPage from "@/pages/MissedConnectionRequestPage";
import AdminIntroductionsPage from "@/pages/admin/AdminIntroductionsPage";
import AdminIntroductionDetailPage from "@/pages/admin/AdminIntroductionDetailPage";

// Bundle 136 P0-4 - QA cockpit page (preview-only super-admin surface).
import AdminQAPage from "@/pages/admin/AdminQAPage";
// Bundle 139 - Stripe compliance ops surfaces.
// Bundle 142 - Registrations landing page (event-scoped).
import AdminRegistrationsLandingPage from "@/pages/admin/AdminRegistrationsLandingPage";

// Bundle 141 - 4 admin section landing pages + email webhook audit.
import AdminOperationsLandingPage from "@/pages/admin/AdminOperationsLandingPage";
import AdminAnalyticsLandingPage from "@/pages/admin/AdminAnalyticsLandingPage";
import AdminSettingsLandingPage from "@/pages/admin/AdminSettingsLandingPage";
import AdminToolsLandingPage from "@/pages/admin/AdminToolsLandingPage";
import AdminEmailWebhookAuditPage from "@/pages/admin/AdminEmailWebhookAuditPage";

import AdminAgeVerificationPendingPage from "@/pages/admin/AdminAgeVerificationPendingPage";
import AdminFeatureFlagsPage from "@/pages/admin/AdminFeatureFlagsPage";
import AdminIntegrationSecretsPage from "@/pages/admin/AdminIntegrationSecretsPage";

// Bundle 79 - Phone-first call queue + digital confirmation + connections.
import AdminCallQueuePage from "@/pages/admin/AdminCallQueuePage";
import IntroductionConfirmPage from "@/pages/IntroductionConfirmPage";
import MyConnectionsPage from "@/pages/MyConnectionsPage";

// Bundle 80 - Follow-ups + Success Stories + Test Mode.
import ConnectionFollowupPage from "@/pages/ConnectionFollowupPage";
import AdminSuccessStoriesPage from "@/pages/admin/AdminSuccessStoriesPage";
import AdminTestModePage from "@/pages/admin/AdminTestModePage";

// Bundle 80.2 - Public testimonials showcase + admin controls.
import TestimonialsPage from "@/pages/TestimonialsPage";
import AdminTestimonialsShowcasePage from "@/pages/admin/AdminTestimonialsShowcasePage";
import AdminTestimonialApprovalQueuePage from "@/pages/admin/AdminTestimonialApprovalQueuePage";

// Bundle 83 - Public /stories page.
import StoriesPage from "@/pages/StoriesPage";

// Bundle 75 Task 3 - super-admin consumer audit ops page.
import AdminConsumerAuditPage from "@/pages/admin/AdminConsumerAuditPage";

// Bundle 76 - Post-event survey.
import SurveyTokenPage from "@/pages/SurveyTokenPage";
import AdminSurveyConsolePage from "@/pages/admin/AdminSurveyConsolePage";

// Design System Demo
import DesignSystemDemo from "@/pages/DesignSystemDemo";

// Route guards
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleProtectedRoute from "@/components/RoleProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";

// F2.e - global progression event listener (level-up toast + rank-up
// modal + achievement unlock reveal). Must live inside BrowserRouter so
// its `useNavigate` hook works, and inside AuthProvider so it can react
// to authenticated user actions.
import ProgressionEventListener from "@/components/progression/ProgressionEventListener";
// Sonner Toaster host - toasts (level-up, single-unlock) will not
// render without this mounted. Kept minimal at app root.
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { PixelProvider } from "@/contexts/PixelContext";

// Bundle 138 P0-4 - Age gate grandfathering modal. Renders only when
// server signals via /auth/me `age_gate_prompt=true`.
import AgeGateModal from "@/components/AgeGateModal";
// Bundle 160 P0 - Signup completion modal. Renders when
// /auth/me signals `signup_completion_required=true` (new users
// from Google OAuth / magic link / register-from-rsvp without
// DOB). Existing users unaffected (flag defaults to false).
import CompleteSignupModal from "@/components/CompleteSignupModal";
// Bundle 161 - Soft in-app nudge banner for users who dismissed
// the modal. Rendered globally at the top of the app.
import IncompleteProfileBanner from "@/components/IncompleteProfileBanner";
import { useAuth } from "@/contexts/AuthContext";

// Bundle 56 - ProfileNudgeModal removed from the global mount. The
// nudge overlapped the RSVP form's DOB / photo / social fields and
// felt redundant. Backend endpoints stay live in case we want the
// UI back; the import is dropped so the bundle does not ship it.

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <PixelProvider>
            <ErrorBoundary>
              <ProgressionEventListener />
              <SonnerToaster />
              <AgeGateHost />
              <SignupCompletionHost />
              <AppRouter />
            </ErrorBoundary>
          </PixelProvider>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

/**
 * Bundle 138 P0-4 - Global age-gate host. Renders the blocking
 * grandfathering modal when the server signals `age_gate_prompt=true`
 * for the current user (feature-flag gated on the backend).
 */
function AgeGateHost() {
  const { user, refresh } = useAuth();
  const open = !!(user && user.age_gate_prompt);
  return (
    <AgeGateModal
      open={open}
      onConfirmed={() => { if (typeof refresh === 'function') refresh(); }}
    />
  );
}

/**
 * Bundle 160 P0 - Global signup-completion host. Renders the
 * blocking CompleteSignupModal when the server signals
 * `signup_completion_required=true` (Google OAuth / magic link /
 * register-from-rsvp new users). Existing users default to false
 * so they are unaffected.
 *
 * Bundle 161 - Adds the soft in-app banner AND a manual `open`
 * override so the "Complete now" CTA on the banner reopens the
 * modal even after the user dismissed once.
 */
function SignupCompletionHost() {
  const { user, refresh } = useAuth();
  const [manualOpen, setManualOpen] = React.useState(false);
  const [userDismissed, setUserDismissed] = React.useState(false);
  const flagged = !!(user && user.signup_completion_required);
  // Bundle 170 hotfix (Fire 1b): if the user dismisses the modal we
  // stop showing the auto-open until either the auth state refreshes
  // to `signup_completion_required=false` (they completed elsewhere)
  // or they explicitly re-open via the IncompleteProfileBanner CTA.
  // Server-side flag stays true so RSVP / profile writes remain
  // blocked - the modal just does not trap them anymore.
  const modalOpen = manualOpen || (flagged && !userDismissed);
  return (
    <>
      <IncompleteProfileBanner
        user={user}
        onCompleteClicked={() => {
          setUserDismissed(false);
          setManualOpen(true);
        }}
      />
      <CompleteSignupModal
        open={modalOpen}
        user={user}
        onCompleted={() => {
          setManualOpen(false);
          setUserDismissed(false);
          if (typeof refresh === 'function') refresh();
        }}
        onDismissed={() => {
          setManualOpen(false);
          setUserDismissed(true);
        }}
      />
    </>
  );
}

/**
 * Emergent Google Auth race-condition fix (per auth playbook).
 *
 * The OAuth provider returns the user to their originating URL with
 * `#session_id=xxx` in the fragment. We detect this SYNCHRONOUSLY (during
 * render, NOT in useEffect) so AuthCallback mounts before any protected-
 * route logic kicks in and before AuthContext calls /auth/me.
 */
function AppRouter() {
  const location = useLocation();
  // F2 referral (2.a): capture ?ref=<code> from the landing URL once per session.
  useReferralCapture();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
            {/* ---------- PUBLIC surfaces (no auth) ---------- */}
            <Route element={<PublicLayout />}>
              <Route index element={<HomePage />} />
              <Route path="/events" element={<EventsPublicPage />} />
              <Route path="/events/:slug" element={<EventDetailPage />} />
              <Route path="/events/:slug/rsvp" element={<RsvpMultiStepPage />} />
              <Route path="/events/:slug/survey" element={<SurveyPage />} />
              <Route path="/rsvp/edit" element={<RsvpEditPage />} />
              <Route path="/rsvp/verify" element={<RsvpVerifyPage />} />
              <Route path="/rsvp/:registrationId/complete-profile" element={<ProfileCompletionGate />} />
              <Route path="/rsvp/resume/:token" element={<RsvpResumePage />} />
              {/* Bundle 153a - public intro suggestion confirmation. */}
              <Route path="/intro/suggestion/:token" element={<IntroSuggestionConfirmPage />} />
              <Route path="/auth/magic-link" element={<MagicLinkPage />} />
              <Route path="/auth/magic-link/verify" element={<MagicLinkPage />} />
              <Route path="/auth/set-password" element={<ProtectedRoute><SetPasswordPage /></ProtectedRoute>} />
              <Route path="/settings/security" element={<ProtectedRoute><SecuritySettingsPage /></ProtectedRoute>} />
              <Route path="/payment/success" element={<PaymentSuccessPage />} />
              <Route path="/payment/cancel" element={<PaymentCancelPage />} />
              {/* Bundle 75 Task 4 - Stripe compliance + community safety. */}
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/terms-and-conditions" element={<TermsPage />} />
              <Route path="/tos" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/law-enforcement" element={<LawEnforcementPage />} />
              {/* Bundle 138 - Stripe compliance: AUP + public Pricing. */}
              <Route path="/acceptable-use" element={<AcceptableUsePolicyPage />} />
              <Route path="/acceptable-use-policy" element={<AcceptableUsePolicyPage />} />
              <Route path="/aup" element={<AcceptableUsePolicyPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              {/* Bundle 80.2 - Public testimonials showcase. */}
              <Route path="/testimonials" element={<TestimonialsPage />} />
              {/* Bundle 83 - Public /stories page. */}
              <Route path="/stories" element={<StoriesPage />} />
              <Route path="/introductions/request/:slug" element={<MissedConnectionRequestPage />} />
              <Route path="/introductions/confirm/:token" element={<IntroductionConfirmPage />} />
              <Route path="/connections" element={
                <RoleProtectedRoute allowedRoles={["super_admin", "admin", "user"]}>
                  <MyConnectionsPage />
                </RoleProtectedRoute>
              } />
              <Route path="/connection-followups/:token" element={<ConnectionFollowupPage />} />
              {/* Bundle 76 - Public post-event survey (HMAC opaque token). */}
              {/* Bundle 84 Task 5 - moved out of PublicLayout below. */}
            </Route>

            {/* Bundle 84 Task 5 - minimal, privacy-first layout for
                signed survey links (no marketing chrome, no trackers,
                no nav / footer CTAs). */}
            <Route element={<SurveyMinimalLayout />}>
              <Route path="/survey/replaced/:oldToken" element={<SurveyReplacedPage />} />
              <Route path="/survey/:token" element={<SurveyTokenPage />} />
            </Route>

            {/* ---------- AUTH (public) ---------- */}
            <Route path="/auth" element={<AuthLayout />}>
              <Route path="login" element={<LoginPage />} />
              <Route path="register" element={<RegisterPage />} />
            </Route>

            {/* ---------- AUTH-GATED - signed-in users ---------- */}
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <OnboardingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/my-events" replace />} />
            </Route>

            <Route
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/my-events" element={<MyEventsPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/verification" element={<VerificationPage />} />
              {/* Kept for Phase 8 - hidden from nav */}
              <Route path="/matches" element={<MatchesPage />} />
              <Route path="/messages" element={<MessagesPage />} />
            </Route>

            {/* ---------- ADMIN (role-gated) ---------- */}
            <Route
              element={
                <RoleProtectedRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminLayout />
                </RoleProtectedRoute>
              }
            >
              <Route path="/admin" element={<Navigate to="/admin/events" replace />} />
              <Route path="/admin/events" element={<AdminEventsListPage />} />
              <Route path="/admin/events/:id/edit" element={<AdminEventEditPage />} />
              <Route path="/admin/events/:id/registrations" element={<AdminRegistrationsPage />} />
              <Route path="/admin/events/:id/survey-results" element={<AdminSurveyResultsPage />} />
              <Route path="/admin/events/:id/connections" element={<AdminConnectionsPage />} />
              <Route path="/admin/events/:id/seating" element={<AdminSeatingPage />} />
              {/* Bundle 151 - P0 payment reconciliation queue. */}
              <Route path="/admin/events/:eid/reconciliation" element={<EventReconciliationPage />} />
              {/* Bundle 76 - Full survey console. */}
              <Route path="/admin/events/:id/survey" element={<AdminSurveyConsolePage />} />
              {/* Bundle 98 - super_admin incident admin page. */}
              <Route path="/admin/incident/legacy-survey-recipients" element={<IncidentLegacySurveyRecipientsPage />} />
              {/* Bundle 101 - Survey Ops landing + Respondents + Campaign + Pre-Bundle-89. */}
              <Route path="/admin/surveys" element={
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminSurveysLandingPage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/events/:eventId/survey/respondents" element={
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminSurveyRespondentsPage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/events/:eventId/survey/campaign" element={
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminSurveyCampaignPage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/incident/pre-bundle89-recipients" element={
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminIncidentPreBundle89Page />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/incident/drift-scan" element={
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminDriftScanPage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/survey/email-templates" element={
                <RoleProtectedRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminEmailTemplatesPage />
                </RoleProtectedRoute>
              } />
              {/* Bundle 108 fold-in - read-only diagnostic for the
                  multi-row Drift Scan mystery. */}
              <Route path="/admin/_debug/campaigns" element={
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminDebugCampaignsPage />
                </RoleProtectedRoute>
              } />
              {/* Bundle 128 - Dedicated Incident Actions page (was
                  invisibly nested under /_debug/campaigns in Bundle 127). */}
              <Route path="/admin/incidents" element={
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminIncidentsPage />
                </RoleProtectedRoute>
              } />
              {/* Bundle 102 - Draft-first follow-ups review. */}
              <Route path="/admin/followups/pending" element={
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminFollowupsPendingPage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/events/:id/nudge" element={
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminEventNudgePage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/nudge" element={
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminEventNudgePage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/taxonomies" element={<AdminTaxonomiesPage />} />
              {/* Bundle 141 - Analytics section landing; the previous
                  data dashboard moved to /admin/analytics/overview. */}
              <Route path="/admin/analytics" element={<AdminAnalyticsLandingPage />} />
              <Route path="/admin/analytics/overview" element={<AdminAnalyticsPage />} />
              <Route path="/admin/operations" element={<AdminOperationsLandingPage />} />
              <Route path="/admin/settings" element={
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminSettingsLandingPage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/tools" element={
                <RoleProtectedRoute allowedRoles={["super_admin", "admin"]}>
                  <AdminToolsLandingPage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/settings/email-webhooks" element={
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminEmailWebhookAuditPage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/introductions" element={
                <RoleProtectedRoute allowedRoles={["super_admin", "admin"]}>
                  <AdminIntroductionsPage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/introductions/:intro_id" element={
                <RoleProtectedRoute allowedRoles={["super_admin", "admin"]}>
                  <AdminIntroductionDetailPage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/introductions/calls" element={
                <RoleProtectedRoute allowedRoles={["super_admin", "admin"]}>
                  <AdminCallQueuePage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/qa" element={
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminQAPage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/age-verification-pending" element={
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminAgeVerificationPendingPage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/settings/feature-flags" element={
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminFeatureFlagsPage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/settings/integrations" element={
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminIntegrationSecretsPage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/success-stories" element={
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminSuccessStoriesPage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/testimonials/showcase" element={
                <RoleProtectedRoute allowedRoles={["super_admin", "admin"]}>
                  <AdminTestimonialsShowcasePage />
                </RoleProtectedRoute>
              } />
              {/* Bundle 156b - testimonial approval queue */}
              <Route path="/admin/testimonials/approval-queue" element={
                <RoleProtectedRoute allowedRoles={["super_admin", "admin"]}>
                  <AdminTestimonialApprovalQueuePage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/test-mode" element={
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminTestModePage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/progression" element={
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminProgressionPage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/referrals" element={
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminReferralsPage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/pixels" element={<AdminPixelsPage />} />
              <Route path="/admin/pixels/analytics" element={<Navigate to="/admin/analytics?tab=pixel" replace />} />
              <Route path="/admin/health" element={<AdminDataHealthPage />} />
              <Route path="/admin/health/:cardId/rows" element={<AdminHealthDrilldownPage />} />
              <Route path="/admin/maintenance" element={
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminMaintenancePage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/advanced-tools" element={
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminMaintenancePage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/integrations" element={<AdminIntegrationsPage />} />
              <Route path="/admin/audit" element={<AdminAuditPage />} />
              <Route path="/admin/pricing-audit" element={
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminPricingAuditPage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/broken-media" element={
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminBrokenMediaReportPage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/users" element={<AdminUsersPage />} />
              <Route path="/admin/users/incomplete-profiles" element={
                <RoleProtectedRoute allowedRoles={["admin","super_admin"]}>
                  <AdminIncompleteProfilesPage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/users/broken-names" element={
                <RoleProtectedRoute allowedRoles={["admin","super_admin"]}>
                  <AdminBrokenNamesPage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/registrations" element={<AdminRegistrationsLandingPage />} />
              <Route path="/admin/users/:userId" element={<AdminUserDetailPage />} />
              <Route path="/admin/user-roles" element={
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminUserRolesPage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/payments" element={<AdminPaymentsPage />} />
              <Route path="/admin/payments/by-event" element={<AdminPaymentsByEventPage />} />
              <Route path="/admin/payments/auto-verify-config" element={
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminZelleAutoVerifyPage />
                </RoleProtectedRoute>
              } />
              <Route path="/admin/verifications" element={<AdminVerificationPage />} />
              {/* Bundle 75 Task 3 - super-admin ops feed of every helper consumer. */}
              <Route path="/admin/ops/consumer-audit" element={
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminConsumerAuditPage />
                </RoleProtectedRoute>
              } />
            </Route>

            {/* ---------- CHECK-IN STAFF (also open to admin/super_admin) ---------- */}
            <Route
              path="/check-in"
              element={
                <RoleProtectedRoute allowedRoles={["check_in_staff", "admin", "super_admin"]}>
                  <CheckInPage />
                </RoleProtectedRoute>
              }
            />
            {/* Per-event scanner. Presets the event context via URL param. */}
            <Route
              path="/admin/events/:eventId/check-in"
              element={
                <RoleProtectedRoute allowedRoles={["check_in_staff", "admin", "super_admin"]}>
                  <CheckInPage />
                </RoleProtectedRoute>
              }
            />

            {/* Design System Demo */}
            <Route path="/design-system" element={<DesignSystemDemo />} />

            {/* Progression guide - public discovery page */}
            <Route path="/how-progression-works" element={<ProgressionGuidePage />} />
            <Route path="/progression-guide" element={<ProgressionGuidePage />} />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
  );
}

export default App;
